import { useState, useMemo, useCallback } from 'react';
import { Loader2, Package, Trash2, Sparkles, Download, ArrowLeft } from 'lucide-react';
import { useDeals } from '@/lib/hooks';
import { Deal, DealStage } from '@/types/database';
import { formatCurrency, formatPercentage, cn } from '@/lib/utils';

interface BundlePosition {
  deal: Deal;
  weight: number;
}

type BundleTheme = 'sector' | 'stage' | 'vintage' | 'mixed';

interface BundleThemeOption {
  value: BundleTheme;
  label: string;
  description: string;
  icon: string;
}

const bundleThemeOptions: BundleThemeOption[] = [
  {
    value: 'sector',
    label: 'Sector Focus',
    description: 'Technology, Healthcare, Consumer, etc.',
    icon: '🎯',
  },
  {
    value: 'stage',
    label: 'Stage-Based',
    description: 'Seed, Growth, Late-Stage',
    icon: '📈',
  },
  {
    value: 'vintage',
    label: 'Vintage Year',
    description: '2018-2020, 2021-2023, etc.',
    icon: '📅',
  },
  {
    value: 'mixed',
    label: 'Mixed Strategy',
    description: 'Diversified across multiple themes',
    icon: '🎨',
  },
];

export function BundlePage(): React.ReactElement {
  const [selectedPositions, setSelectedPositions] = useState<BundlePosition[]>([]);
  const [bundleTheme, setBundleTheme] = useState<BundleTheme>('mixed');
  const [bundleName, setBundleName] = useState<string>('');
  const [targetMarkup, setTargetMarkup] = useState<string>('15');
  const [showPitchDeck, setShowPitchDeck] = useState<boolean>(false);

  // Fetch all closed/acquired positions eligible for bundling
  const { data: closedDeals, isLoading, error } = useDeals({
    stage: DealStage.CLOSED
  });

  const eligiblePositions = useMemo(() => {
    return closedDeals?.data.filter(deal =>
      deal.true_discount !== null &&
      deal.fee_drag !== null
    ) ?? [];
  }, [closedDeals]);

  // Calculate bundle metrics
  const bundleMetrics = useMemo(() => {
    if (selectedPositions.length === 0) {
      return {
        totalNAV: 0,
        blendedTrueDiscount: 0,
        aggregateFeeDrag: 0,
        expectedNetDiscount: 0,
        suggestedResalePrice: 0,
        targetMarkup: 0,
        projectedProfit: 0,
      };
    }

    const totalNAV = selectedPositions.reduce((sum, pos) => sum + pos.deal.target_value, 0);

    // Weighted average calculations
    const blendedTrueDiscount = selectedPositions.reduce((sum, pos) => {
      const weight = pos.deal.target_value / totalNAV;
      return sum + (pos.deal.true_discount ?? 0) * weight;
    }, 0);

    const aggregateFeeDrag = selectedPositions.reduce((sum, pos) => {
      const weight = pos.deal.target_value / totalNAV;
      return sum + (pos.deal.fee_drag ?? 0) * weight;
    }, 0);

    const expectedNetDiscount = blendedTrueDiscount - aggregateFeeDrag;
    const markupRate = parseFloat(targetMarkup) / 100;
    const suggestedResalePrice = totalNAV * (1 + markupRate);
    const projectedProfit = suggestedResalePrice - totalNAV;

    return {
      totalNAV,
      blendedTrueDiscount,
      aggregateFeeDrag,
      expectedNetDiscount,
      suggestedResalePrice,
      targetMarkup: markupRate,
      projectedProfit,
    };
  }, [selectedPositions, targetMarkup]);

  const handleTogglePosition = useCallback((deal: Deal) => {
    setSelectedPositions(prev => {
      const existing = prev.find(p => p.deal.id === deal.id);

      if (existing) {
        // Remove position
        return prev.filter(p => p.deal.id !== deal.id);
      } else {
        // Add position (max 5)
        if (prev.length >= 5) {
          alert('Maximum 5 positions per bundle');
          return prev;
        }

        const newWeight = 1 / (prev.length + 1);
        const updatedPositions = prev.map(p => ({
          ...p,
          weight: p.weight * (prev.length / (prev.length + 1)),
        }));

        return [...updatedPositions, { deal, weight: newWeight }];
      }
    });
  }, []);

  const handleRemovePosition = useCallback((dealId: string) => {
    setSelectedPositions(prev => {
      const filtered = prev.filter(p => p.deal.id !== dealId);
      if (filtered.length === 0) return [];

      // Rebalance weights
      const equalWeight = 1 / filtered.length;
      return filtered.map(p => ({ ...p, weight: equalWeight }));
    });
  }, []);

  const handleGeneratePitchDeck = useCallback(() => {
    if (selectedPositions.length < 3) {
      alert('Please select at least 3 positions to generate a pitch deck');
      return;
    }
    setShowPitchDeck(true);
  }, [selectedPositions]);

  const handleDownloadPitchDeck = useCallback(() => {
    // In a real implementation, this would generate a PDF or presentation file
    alert('Pitch deck download would be triggered here');
  }, []);

  const canCreateBundle = selectedPositions.length >= 3 && selectedPositions.length <= 5 && bundleName.trim() !== '';

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">Failed to load eligible positions</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-3">
            <Package className="h-8 w-8 text-blue-600" />
            <h2 className="text-2xl font-bold text-gray-900">Multi-Fund Bundling</h2>
          </div>
          <p className="text-gray-600 mt-1">
            Create bundled positions from 3-5 closed deals for resale - "a whole new asset class"
          </p>
        </div>
        {selectedPositions.length > 0 && (
          <button
            onClick={handleGeneratePitchDeck}
            disabled={!canCreateBundle}
            className={cn(
              'flex items-center gap-2 px-4 py-2 rounded-md font-medium transition-colors',
              canCreateBundle
                ? 'bg-purple-600 text-white hover:bg-purple-700'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            )}
          >
            <Sparkles className="h-5 w-5" />
            Generate Pitch Deck
          </button>
        )}
      </div>

      {/* Bundle Configuration */}
      {selectedPositions.length > 0 && (
        <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-lg border-2 border-blue-200 p-6 space-y-6">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">Bundle Configuration</h3>
            <span className="text-sm font-medium text-blue-600">
              {selectedPositions.length} / 5 positions selected
            </span>
          </div>

          {/* Bundle Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Bundle Name
            </label>
            <input
              type="text"
              value={bundleName}
              onChange={(e) => setBundleName(e.target.value)}
              placeholder="e.g., Tech Growth 2020-2022 Portfolio"
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Theme Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Bundle Theme
            </label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {bundleThemeOptions.map((option) => (
                <button
                  key={option.value}
                  onClick={() => setBundleTheme(option.value)}
                  className={cn(
                    'p-4 rounded-lg border-2 transition-all text-left',
                    bundleTheme === option.value
                      ? 'border-blue-500 bg-blue-50 shadow-md'
                      : 'border-gray-200 bg-white hover:border-gray-300'
                  )}
                >
                  <div className="text-2xl mb-2">{option.icon}</div>
                  <div className="font-medium text-gray-900 text-sm">{option.label}</div>
                  <div className="text-xs text-gray-500 mt-1">{option.description}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Target Markup */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Target Markup for Resale (%)
            </label>
            <input
              type="number"
              min="5"
              max="50"
              step="1"
              value={targetMarkup}
              onChange={(e) => setTargetMarkup(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <p className="text-xs text-gray-500 mt-1">
              Typical range: 10-25% for bundled secondary positions
            </p>
          </div>

          {/* Bundle Metrics */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t border-gray-200">
            <div>
              <div className="text-xs font-medium text-gray-500 uppercase tracking-wider">Total NAV</div>
              <div className="text-2xl font-bold text-gray-900 mt-1">
                {formatCurrency(bundleMetrics.totalNAV)}
              </div>
            </div>
            <div>
              <div className="text-xs font-medium text-gray-500 uppercase tracking-wider">Blended TRUE %</div>
              <div className="text-2xl font-bold text-blue-600 mt-1">
                {formatPercentage(bundleMetrics.blendedTrueDiscount)}
              </div>
            </div>
            <div>
              <div className="text-xs font-medium text-gray-500 uppercase tracking-wider">Aggregate Fee Drag</div>
              <div className="text-2xl font-bold text-orange-600 mt-1">
                {formatPercentage(bundleMetrics.aggregateFeeDrag)}
              </div>
            </div>
            <div>
              <div className="text-xs font-medium text-gray-500 uppercase tracking-wider">Net Discount</div>
              <div className="text-2xl font-bold text-green-600 mt-1">
                {formatPercentage(bundleMetrics.expectedNetDiscount)}
              </div>
            </div>
          </div>

          {/* Resale Projection */}
          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <div className="grid grid-cols-3 gap-4">
              <div>
                <div className="text-xs font-medium text-gray-500 uppercase tracking-wider">Acquisition Cost</div>
                <div className="text-xl font-bold text-gray-900 mt-1">
                  {formatCurrency(bundleMetrics.totalNAV)}
                </div>
              </div>
              <div>
                <div className="text-xs font-medium text-gray-500 uppercase tracking-wider">Resale Price ({targetMarkup}% markup)</div>
                <div className="text-xl font-bold text-purple-600 mt-1">
                  {formatCurrency(bundleMetrics.suggestedResalePrice)}
                </div>
              </div>
              <div>
                <div className="text-xs font-medium text-gray-500 uppercase tracking-wider">Projected Profit</div>
                <div className="text-xl font-bold text-green-600 mt-1">
                  {formatCurrency(bundleMetrics.projectedProfit)}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Selected Positions */}
      {selectedPositions.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Selected Positions</h3>
          <div className="space-y-3">
            {selectedPositions.map((position) => (
              <div
                key={position.deal.id}
                className="flex items-center justify-between p-4 bg-blue-50 rounded-lg border border-blue-200"
              >
                <div className="flex-1">
                  <div className="font-medium text-gray-900">{position.deal.name}</div>
                  <div className="text-sm text-gray-600 mt-1">{position.deal.seller_name}</div>
                  <div className="flex items-center gap-4 mt-2 text-xs">
                    <span className="text-gray-700">
                      NAV: <span className="font-medium">{formatCurrency(position.deal.target_value)}</span>
                    </span>
                    <span className="text-blue-600">
                      TRUE: <span className="font-medium">{formatPercentage(position.deal.true_discount ?? 0)}</span>
                    </span>
                    <span className="text-orange-600">
                      Fee Drag: <span className="font-medium">{formatPercentage(position.deal.fee_drag ?? 0)}</span>
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => handleRemovePosition(position.deal.id)}
                  className="ml-4 p-2 text-red-600 hover:bg-red-100 rounded-md transition-colors"
                  aria-label={`Remove ${position.deal.name}`}
                >
                  <Trash2 className="h-5 w-5" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Eligible Positions Grid */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">
            Available Closed Positions ({eligiblePositions.length})
          </h3>
          <p className="text-sm text-gray-500">
            Click to add positions to your bundle (3-5 required)
          </p>
        </div>

        {eligiblePositions.length === 0 ? (
          <div className="text-center py-12">
            <Package className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 mb-2">No eligible positions for bundling</p>
            <p className="text-sm text-gray-400">
              Close some deals first to create bundle opportunities
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {eligiblePositions.map((deal) => {
              const isSelected = selectedPositions.some(p => p.deal.id === deal.id);

              return (
                <button
                  key={deal.id}
                  onClick={() => handleTogglePosition(deal)}
                  disabled={!isSelected && selectedPositions.length >= 5}
                  className={cn(
                    'p-4 rounded-lg border-2 transition-all text-left',
                    isSelected
                      ? 'border-blue-500 bg-blue-50 shadow-md'
                      : selectedPositions.length >= 5
                      ? 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-50'
                      : 'border-gray-200 hover:border-blue-300 hover:shadow-sm'
                  )}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="font-medium text-gray-900 text-sm line-clamp-1">
                      {deal.name}
                    </div>
                    {isSelected && (
                      <div className="ml-2 flex-shrink-0 w-5 h-5 bg-blue-600 rounded-full flex items-center justify-center">
                        <span className="text-white text-xs font-bold">✓</span>
                      </div>
                    )}
                  </div>

                  <div className="text-xs text-gray-600 mb-3 line-clamp-1">
                    {deal.seller_name}
                  </div>

                  <div className="space-y-1">
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-500">NAV:</span>
                      <span className="font-medium text-gray-900">
                        {formatCurrency(deal.target_value)}
                      </span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-500">TRUE %:</span>
                      <span className="font-medium text-blue-600">
                        {formatPercentage(deal.true_discount ?? 0)}
                      </span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-500">Fee Drag:</span>
                      <span className="font-medium text-orange-600">
                        {formatPercentage(deal.fee_drag ?? 0)}
                      </span>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Pitch Deck Modal */}
      {showPitchDeck && (
        <PitchDeckModal
          bundleName={bundleName}
          theme={bundleTheme}
          positions={selectedPositions}
          metrics={bundleMetrics}
          onClose={() => setShowPitchDeck(false)}
          onDownload={handleDownloadPitchDeck}
        />
      )}
    </div>
  );
}

interface PitchDeckModalProps {
  bundleName: string;
  theme: BundleTheme;
  positions: BundlePosition[];
  metrics: {
    totalNAV: number;
    blendedTrueDiscount: number;
    aggregateFeeDrag: number;
    expectedNetDiscount: number;
    suggestedResalePrice: number;
    targetMarkup: number;
    projectedProfit: number;
  };
  onClose: () => void;
  onDownload: () => void;
}

function PitchDeckModal({
  bundleName,
  theme,
  positions,
  metrics,
  onClose,
  onDownload,
}: PitchDeckModalProps): React.ReactElement {
  const themeLabel = bundleThemeOptions.find(t => t.value === theme)?.label ?? 'Mixed Strategy';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6 rounded-t-lg">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold">{bundleName || 'Untitled Bundle'}</h2>
              <p className="text-blue-100 mt-1">{themeLabel} Bundle - {positions.length} Positions</p>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:bg-white/20 rounded-lg p-2 transition-colors"
            >
              <ArrowLeft className="h-6 w-6" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Executive Summary */}
          <section>
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-purple-600" />
              Executive Summary
            </h3>
            <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-lg p-6">
              <p className="text-gray-700 leading-relaxed">
                This bundled portfolio represents a curated collection of {positions.length} secondary market
                positions with a combined NAV of <span className="font-bold">{formatCurrency(metrics.totalNAV)}</span>.
                The portfolio offers a blended TRUE discount of{' '}
                <span className="font-bold text-blue-600">{formatPercentage(metrics.blendedTrueDiscount)}</span>{' '}
                with aggregate fee drag of{' '}
                <span className="font-bold text-orange-600">{formatPercentage(metrics.aggregateFeeDrag)}</span>,
                resulting in a net expected discount of{' '}
                <span className="font-bold text-green-600">{formatPercentage(metrics.expectedNetDiscount)}</span>.
              </p>
            </div>
          </section>

          {/* Key Metrics */}
          <section>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Key Metrics</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <div className="text-xs font-medium text-gray-500 uppercase">Total NAV</div>
                <div className="text-xl font-bold text-gray-900 mt-2">
                  {formatCurrency(metrics.totalNAV)}
                </div>
              </div>
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <div className="text-xs font-medium text-gray-500 uppercase">Blended TRUE %</div>
                <div className="text-xl font-bold text-blue-600 mt-2">
                  {formatPercentage(metrics.blendedTrueDiscount)}
                </div>
              </div>
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <div className="text-xs font-medium text-gray-500 uppercase">Resale Price</div>
                <div className="text-xl font-bold text-purple-600 mt-2">
                  {formatCurrency(metrics.suggestedResalePrice)}
                </div>
              </div>
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <div className="text-xs font-medium text-gray-500 uppercase">Projected Profit</div>
                <div className="text-xl font-bold text-green-600 mt-2">
                  {formatCurrency(metrics.projectedProfit)}
                </div>
              </div>
            </div>
          </section>

          {/* Portfolio Composition */}
          <section>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Portfolio Composition</h3>
            <div className="space-y-3">
              {positions.map((position, index) => (
                <div key={position.deal.id} className="bg-white border border-gray-200 rounded-lg p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-bold text-gray-400">#{index + 1}</span>
                        <span className="font-semibold text-gray-900">{position.deal.name}</span>
                      </div>
                      <div className="text-sm text-gray-600 mt-1">{position.deal.seller_name}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-medium text-gray-900">
                        {formatCurrency(position.deal.target_value)}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        {formatPercentage(position.deal.target_value / metrics.totalNAV)} of total
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-4 mt-3 pt-3 border-t border-gray-100">
                    <div>
                      <div className="text-xs text-gray-500">TRUE Discount</div>
                      <div className="text-sm font-medium text-blue-600">
                        {formatPercentage(position.deal.true_discount ?? 0)}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">Fee Drag</div>
                      <div className="text-sm font-medium text-orange-600">
                        {formatPercentage(position.deal.fee_drag ?? 0)}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">Net Discount</div>
                      <div className="text-sm font-medium text-green-600">
                        {formatPercentage((position.deal.true_discount ?? 0) - (position.deal.fee_drag ?? 0))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Investment Highlights */}
          <section>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Investment Highlights</h3>
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <ul className="space-y-2 text-sm text-gray-700">
                <li className="flex items-start gap-2">
                  <span className="text-green-600 font-bold mt-0.5">✓</span>
                  <span>
                    <span className="font-medium">Diversification:</span> Exposure across {positions.length} different
                    fund positions reduces concentration risk
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-600 font-bold mt-0.5">✓</span>
                  <span>
                    <span className="font-medium">Attractive Pricing:</span> Blended TRUE discount of{' '}
                    {formatPercentage(metrics.blendedTrueDiscount)} with controlled fee drag
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-600 font-bold mt-0.5">✓</span>
                  <span>
                    <span className="font-medium">Immediate Access:</span> All positions closed and ready for transfer
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-600 font-bold mt-0.5">✓</span>
                  <span>
                    <span className="font-medium">Thematic Strategy:</span> {themeLabel} approach provides coherent
                    investment narrative
                  </span>
                </li>
              </ul>
            </div>
          </section>

          {/* Action Buttons */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
            >
              Close
            </button>
            <button
              onClick={onDownload}
              className="px-6 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-md hover:from-blue-700 hover:to-purple-700 transition-colors flex items-center gap-2"
            >
              <Download className="h-5 w-5" />
              Download Pitch Deck
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
